import React, {
  useState,
  useEffect,
  SyntheticEvent,
  Dispatch,
  SetStateAction,
} from "react";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "react-query";
import { ServiceRepairObj } from "../../../../GlobalTypes";
import {
  editService,
  submitService,
  getUser,
  uploadServiceImage,
  getCompanyManagerDealers
} from "../../../../api/ServiceApi";
import { RepairState } from "../../../../pages/DealerZone/DealerZone";
import dealerRoles from "../../../../util/dealerAccounts";

export interface CustomerInfoState {
  customerName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  invoiceNumber: string;
  invoiceDate: string;
  issue: string;
  poNumber: string;
  unitLineNumber: string;
  soNumber: string;
  includeIssueInEmail?: string;
  createdBy?: string;
}

export interface AdminDealer {
  dealer: string;
  dealerRep: string;
  email: string;
  phone: string;
}

function hasError(state: CustomerInfoState, role: string) {
  const requiredFields = [
    state.customerName,
    state.address,
    state.city,
    state.state,
    state.zip,
    state.phone,
    state.issue,
    state.unitLineNumber,
    state.includeIssueInEmail,
    state.email,
  ];

  if (role === "Admin" || role === "Inside Sales") {
    requiredFields.pop();
  }

  if (requiredFields.some((field) => !field)) return true;

  if (!state.poNumber && (!state.invoiceNumber || !state.invoiceDate))
    return true;

  return false;
}

function useServiceSelection(
  id: string,
  snNumber: number,
  setSnNumber: Dispatch<SetStateAction<number>>,
  repair: RepairState,
  setRepair: Dispatch<SetStateAction<RepairState>>
) {
  const theme = useTheme();

  const { isFetching, data } = useQuery(
    "DelerInfo",
    async () => {
      const response = await getUser(id);
      const roles = ["Manager II", "Company Manager"]
      if(roles.includes(response.data.role)){
        if(response.data.role === "Company Manager"){
          const list = await getCompanyManagerDealers(response.data.dealer);
          setDealerList(list.data)
        }else{
          setDealerList(response.data.manager2DealerAccess || [])
        }
        setAdminDealer({
          dealer: response.data.dealer || "",
          dealerRep: response.data.dealerRep || "",
          email: response.data.email || "",
          phone: response.data.phone || ""
        })
      }
      return response.data;
    },
    { refetchOnWindowFocus: false }
  );

  const viewSR = (repair: ServiceRepairObj) => {
    setValue(0);
    setServiceTeam(repair.team || 0);
    setLoadUpload(0);
    setFormNotes(repair.privateNotes || "");
    setSnNumber(repair.snNumber);
    setCustomerInfo({
      customerName: repair.customerName,
      address: repair.address,
      city: repair.city,
      state: repair.state,
      zip: repair.zip,
      phone: repair.phone,
      email: repair.email || "",
      invoiceNumber: repair.invoiceNumber || "",
      invoiceDate: repair.invoiceDate || "",
      issue: repair.issue,
      poNumber: repair.poNumber || "",
      unitLineNumber: repair.unitLineNumber,
      createdBy: repair.createdBy || "",
      soNumber: repair.soNumber || "",
      includeIssueInEmail: repair.includeIssueInEmail || "",
    });
    (repair.uploadedImg1 || repair.uploadedImg2 || repair.uploadedImg3) &&
      setUploadURLs({
        u1: repair.uploadedImg1 || "",
        u2: repair.uploadedImg2 || "",
        u3: repair.uploadedImg3 || "",
      });
    setRepair({ status: repair.status, id: repair._id });
    setAdminDealer({
      dealer: repair.dealer,
      dealerRep: repair.dealerRep,
      email: repair.dealerEmail || "",
      phone: repair.dealerPhone || "",
    });
  };

  const [adminDealer, setAdminDealer] = useState<AdminDealer>({
    dealer: "",
    dealerRep: "",
    email: "",
    phone: "",
  });

  const [dealerList, setDealerList] = useState([]); //used for manager II and company manager roles

  const handleDealerDropdown = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setAdminDealer(prev => ({...prev, dealer: event.target.value}))
  }

  const [error, setError] = useState(false);

  const [openErrModal, setOpenErrModal] = useState(false);

  const [submissionErr, setSE] = useState("");

  const [value, setValue] = useState(0);

  const [uploadURLs, setUploadURLs] = useState({
    u1: "",
    u2: "",
    u3: "",
  });

  const [customerInfo, setCustomerInfo] = useState<CustomerInfoState>({
    customerName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: "",
    invoiceNumber: "",
    invoiceDate: "",
    issue: "",
    poNumber: "",
    unitLineNumber: "",
    soNumber: "",
    includeIssueInEmail: "",
  });

  const [loading, setLoading] = useState(false);

  const [loadUpload, setLoadUpload] = useState(0);

  const [toggleEmail, setToggle] = useState(false);

  const [serviceTeam, setServiceTeam] = useState(0);

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) =>
    setToggle(event.target.checked);

  const resetServiceRepair = () => {
    setSnNumber(0);
    setServiceTeam(0);
    setCustomerInfo({
      customerName: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
      email: "",
      invoiceNumber: "",
      invoiceDate: "",
      issue: "",
      poNumber: "",
      unitLineNumber: "",
      soNumber: "",
    });
    setUploadURLs({
      u1: "",
      u2: "",
      u3: "",
    });
    setFormNotes("");
    repair.status && setRepair({ status: "", id: "" });
    setAdminDealer({
      dealer: "",
      dealerRep: "",
      email: "",
      phone: "",
    });
  };

  useEffect(() => {
    if (!snNumber) return;
    handleMissingFields(customerInfo);
  }, [customerInfo, snNumber]);

  const handleMissingFields = (state: CustomerInfoState) => {
    setError(hasError(state, data.role));
  };

  const handleChangeAdmin = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    setAdminDealer({ ...adminDealer, [name]: e.target.value });
  };

  const handleInfoChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const name = e.target.name;
    setCustomerInfo({ ...customerInfo, [name]: e.target.value });
  };

  const handleIncludeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = e.target;
    if (!checked) return;

    setCustomerInfo({
      ...customerInfo,
      includeIssueInEmail: value,
    });
  };

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
    repair.status && resetServiceRepair();
  };

  const handleUploadSubmit = async (
    event: React.ChangeEvent<HTMLInputElement>,
    imgNumber: number
  ) => {
    setLoadUpload(imgNumber);
    const file = event.target?.files ? event.target.files[0] : null;

    if (!file) return;

    const formData = new FormData();

    formData.append("file", file);
    formData.append("public_id", `ServiceRepair/SN_${snNumber}/${imgNumber}`);

    uploadServiceImage(formData)
      .then((data) => {
        imgNumber === 1
          ? setUploadURLs({ ...uploadURLs, u1: data.data.secure_url })
          : imgNumber === 2
          ? setUploadURLs({ ...uploadURLs, u2: data.data.secure_url })
          : setUploadURLs({ ...uploadURLs, u3: data.data.secure_url });
      })
      .catch((err) => console.log(err))
      .then((upl) => setLoadUpload(0));
  };

  const createSR = async () => {
    setLoading(true);

    function handleError() {
      setLoading(false);
      setOpenErrModal(true);
      handleCloseWarnModal();
      setSE(
        "Problem Occured When Submitting Service Repair. Please Try Again."
      );
    }

    try {
      const serviceSubmitStatus = await submitService(
        snNumber,
        uploadURLs,
        customerInfo,
        ["Dealer", "Manager"].includes(data.role) ? data : adminDealer,
        serviceTeam,
        toggleEmail
      );

      if (
        serviceSubmitStatus.data ===
        "A problem occured when trying to send email."
      ) {
        return handleError();
      }

      setLoading(false);
      handleOpenModal();
      handleCloseWarnModal();
    } catch (err) {
      handleError();
    }
  };

  const editSR = async () => {
    handleCloseWarnModal();
    setLoading(true);

    const serviceRepair = await editService(
      serviceTeam,
      toggleEmail,
      uploadURLs,
      customerInfo,
      repair.id,
      ["Dealer", "Manager"].includes(data.role) ? null : adminDealer
    );

    if (
      "error" in serviceRepair &&
      serviceRepair.error === "Service has Completed status"
    ) {
      setLoading(false);
      setMEE("Can not Update a Service with a Completed Status.");
      return handleOpenModal();
    }

    if (serviceRepair.data === "Service Repair Updated") {
      setLoading(false);
      return handleOpenModal();
    }

    if (serviceRepair.data === "A problem occured when trying to send email.") {
      setLoading(false);
      setMEE("A Problem Occured When Trying to Send Email.");
      return handleOpenModal();
    }
  };

  const [openModal, setOpenModal] = useState(false);

  const [openModalWarn, setOpenModalWarn] = useState(false);

  const handleOpenWarnModal = () => {
    setOpenModalWarn(true);
    setToggle(false);
  };
  const handleCloseWarnModal = () => {
    setOpenModalWarn(false);
    setMEE("");
  };

  const [modalEmailErr, setMEE] = useState("");

  const handleCloseModal = () => {
    resetServiceRepair();
    setMEE("");
    setOpenModal(false);
  };

  const handleOpenModal = () => setOpenModal(true);

  const [formNotes, setFormNotes] = useState("");

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormNotes(e.target.value);
  };

  const disabled = !repair
    ? false
    : !repair.status
    ? false
    : repair.status === "Completed"
    ? true
    : data.signInRole === "Admin" || data.signInRole === "Inside Sales"
    ? false
    : true;

  return {
    disabled,
    handleFormChange,
    formNotes,
    handleCloseModal,
    modalEmailErr,
    handleOpenWarnModal,
    openModalWarn,
    openModal,
    editSR,
    createSR,
    handleUploadSubmit,
    handleChange,
    handleIncludeChange,
    handleInfoChange,
    handleChangeAdmin,
    handleToggle,
    serviceTeam,
    toggleEmail,
    loadUpload,
    loading,
    customerInfo,
    uploadURLs,
    value,
    submissionErr,
    openErrModal,
    error,
    adminDealer,
    viewSR,
    isFetching,
    data,
    theme,
    handleCloseWarnModal,
    setOpenErrModal,
    setAdminDealer,
    setServiceTeam,
    resetServiceRepair,
    dealerList,
    handleDealerDropdown
  };
}

export default useServiceSelection;
